import React, { useMemo } from "react";
import {
  FinancialApplicationFormSectionSummary,
  FinancialApplicationFormSectionSummaryDataRowType,
} from "../FinancialApplicationFormSectionSummary/FinancialApplicationFormSectionSummary";
import { useFinancialApplicationForm } from "@/hooks/useFinancialApplicationForm";
import { useFinancialProducts } from "@/hooks/useFinancialProducts";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";
import { useFieldSchemaToGetDataForSummary } from "../FinancialApplicationFormSectionSummary/useFieldSchemaToGetDataForSummary";
import { FieldInput } from "@/utils/getFieldSchemaData";
import { FIELD_KEYS } from "@/constants";
import { SUBSTEP_PATHS } from "@/config/stepPaths";
import { useFinancialApplicationCoreData } from "@/hooks/useFinancialApplicationCoreData";
import "./AccountDetailsSummary.scss";

export interface AccountDetailsSummaryProps {
  financialApplicationId: string;
  forPersonalAccounts: boolean;
}

export const AccountDetailsSummary: React.FC<AccountDetailsSummaryProps> = ({ 
  financialApplicationId, 
  forPersonalAccounts
}) => {
  const { formValue } = useFinancialApplicationForm(financialApplicationId);
  const financialApplicationApiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);
  const { products } = useFinancialProducts(financialApplicationApiClient);
  const { conditionalFlags } = useFinancialApplicationCoreData(financialApplicationId);

  // Define fields to include based on account type
  const fieldsToInclude: FieldInput[] = useMemo(() => {
    const baseFields: FieldInput[] = [];

    if (forPersonalAccounts) {
      // Personal account fields
      baseFields.push(
        {
          fieldProps: FIELD_KEYS.FINANCIAL_APPLICATION.WILL_HAVE_JOINT_ACCOUNT_OWNERS,
        },
        {
          fieldProps: FIELD_KEYS.FINANCIAL_APPLICATION.WILL_HAVE_PERSONAL_ACCOUNT_BENEFICIARIES,
        }
      );
    } else {
      // Business/IOLTA account fields
      if (conditionalFlags.isKeyPeopleEnabled) {
        baseFields.push(
          {
            fieldProps: FIELD_KEYS.FINANCIAL_APPLICATION.IS_BENEFICIAL_OWNER,
          },
          {
            fieldProps: FIELD_KEYS.FINANCIAL_APPLICATION.IS_AUTHORIZED_SIGNER,
          },
          {
            fieldProps: FIELD_KEYS.FINANCIAL_APPLICATION.IS_CONTROL_PERSON,
          }
        );
      }
    }

    return baseFields;
  }, [forPersonalAccounts, conditionalFlags.isKeyPeopleEnabled]);

  const summaryData = useFieldSchemaToGetDataForSummary(financialApplicationId, fieldsToInclude);

  // Create initial deposit data rows for the appropriate account type
  const initialDepositData =
    formValue?.depositApplications
      ?.map((depositApp) => {
        const product = products.find((p) => p.id === depositApp.financialProductId);
        return { 
          ...depositApp,
          productName: product?.productName,
          isPersonalProduct: product?.isPersonalProduct,
          isBusinessProduct: product?.isBusinessProduct,
          isIoltaProduct: product?.isIoltaProduct,
          isDepositProduct: product?.isDepositProduct,
        };
      })
      .filter((depositApp) => {
        // Filter based on account type
        if (forPersonalAccounts) {
          return depositApp.isPersonalProduct;
        } else {
          return depositApp.isBusinessProduct || depositApp.isIoltaProduct;
        }
      })
      .filter((depositApp) => depositApp.isActive)
      .filter((depositApp) => depositApp.isDepositProduct)
      .map((depositApp) => {
        const productName = depositApp.productName;
        const currentAmount = depositApp.initialDepositAmount;

        return {
          label: `Initial Deposit Amount - ${productName}`,
          value: currentAmount ? `$${currentAmount.toLocaleString()}` : "-",
          type: FinancialApplicationFormSectionSummaryDataRowType.LabelValue as const,
        };
      }) || [];

  // Combine field schema data with initial deposit data
  const combinedData = [...summaryData, ...initialDepositData];

  // If no data to show, return null or a message
  if (combinedData.length === 0) {
    return null;
  }

  return (
    <div className="account-details-summary">
      <FinancialApplicationFormSectionSummary
        title={forPersonalAccounts ? "Personal Account Details" : "Entity Account Details"}
        data={combinedData}
        navigateTo={`/step/${SUBSTEP_PATHS.AboutYou.AccountDetails}`}
      />
    </div>
  );
};
